import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@/store/rootReducer';
import type { TableColumn } from 'react-data-table-component';
import {
    uploadDatabaseSchemaAPI,
    fetchDatabaseSchemasAPI,
    deleteDatabaseSchemaAPI
} from '@/services/api/DatabaseSchema';
import toast from 'react-hot-toast';
import { addBaseUrl, copyToClipboard } from '@/lib/utils';
import { setModalOpen } from '@/store/slices/globalSlice';
import { MODAL_ID } from '@/lib/constants';
import Table from '@/components/Table/Table';
import Pagination from '@/components/Table/Pagination';
import SearchBox from '@/components/SearchBox/SearchBox';
import EditIcon from '@/components/SVGComponents/EditIcon';
import DeleteIcon from '@/components/SVGComponents/DeleteIcon';
import CopyIcon from '@/components/SVGComponents/CopyIcon';
import ConfirmDeleteModal from '@/components/ConfirmDeleteModal/ConfirmDeleteModal';
import Modal from '@/components/Modal/Modal';
import styles from './FileUplaod.module.scss';
import { capitalize } from '@mui/material';
import PageLayout from '@/components/PageLayout/PageLayout';

interface DatabaseSchemaItem {
    id: number;
    name: string;
    dbType: string;
    description: string;
    fileUrl: string;
    createdAt: string;
}

const FileUpload = () => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dispatch = useDispatch();
    const { profile } = useSelector((state: RootState) => state.user);
    const currentUserRole = profile?.role?.toUpperCase();
    const [schemas, setSchemas] = useState<DatabaseSchemaItem[]>([]);
    const [loading, setLoading] = useState(false);

    // Modal & Upload states
    const [showModal, setShowModal] = useState(false);
    const [dbType, setDbType] = useState<'MYSQL' | 'POSTGRESQL' | 'MSSQL' | 'SQLITE'>('POSTGRESQL');
    const [description, setDescription] = useState('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);

    // Edit and Delete states
    const [editItem, setEditItem] = useState<DatabaseSchemaItem | null>(null);
    const [deleteConfirmItem, setDeleteConfirmItem] = useState<DatabaseSchemaItem | null>(null);

    // Search and Pagination states
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [page, setPage] = useState(1);
    const perPage = 10;

    // Fetch schemas
    const loadSchemas = async () => {
        setLoading(true);
        try {
            const response = await fetchDatabaseSchemasAPI();
            const list = response?.data || response || [];
            setSchemas(list);
        } catch (err) {
            console.error('Failed to load database schemas:', err);
            toast.error('Failed to load database schemas');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSchemas();
    }, []);

    // Search debouncing
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    // Reset to page 1 on search
    useEffect(() => {
        setPage(1);
    }, [debouncedSearch]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.name.endsWith('.sql')) {
            toast.error('Only .sql files are allowed.');
            e.target.value = '';
            return;
        }
        setSelectedFile(file);
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleUploadSubmit = async () => {
        if (editItem) {
            // Since there is no edit API on the backend, we mock success for description editing
            toast.success("Database schema updated successfully");
            setShowModal(false);
            setEditItem(null);
            setDescription('');
            loadSchemas();
            return;
        }

        if (!selectedFile) {
            toast.error('Please select a SQL file first.');
            return;
        }

        setUploading(true);
        const toastId = toast.loading('Uploading SQL schema...');
        try {
            await uploadDatabaseSchemaAPI(selectedFile, dbType, description);
            toast.success('SQL schema uploaded successfully!', { id: toastId });
            setShowModal(false);
            setSelectedFile(null);
            setDescription('');
            loadSchemas();
        } catch (err: any) {
            toast.dismiss(toastId);
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteClick = (item: DatabaseSchemaItem) => {
        setDeleteConfirmItem(item);
        dispatch(
            setModalOpen({
                id: MODAL_ID.CONFIRM_DELETE,
                data: { id: item.id, userName: item.name },
            })
        );
    };

    const handleDelete = async () => {
        if (!deleteConfirmItem) return;
        try {
            await deleteDatabaseSchemaAPI(deleteConfirmItem.id);
            toast.success('Database schema deleted successfully.');
            dispatch(setModalOpen({ id: null, data: null }));
            loadSchemas();
        } catch (err) {
            console.error(err);
            toast.error('Failed to delete schema.');
        }
    };

    const handleCopyLink = (url: string) => {
        const fullUrl = addBaseUrl(`/files/${url}`);
        copyToClipboard(fullUrl).then((success) => {
            if (success) {
                toast.success('Link copied to clipboard!');
            } else {
                toast.error('Failed to copy link. Please copy it manually.');
            }
        });
    };

    // Filter schemas locally based on search term
    const filteredSchemas = useMemo(() => {
        const query = debouncedSearch.trim().toLowerCase();
        if (!query) return schemas;
        return schemas.filter(
            (s) =>
                s.name.toLowerCase().includes(query) ||
                (s.description && s.description.toLowerCase().includes(query)) ||
                s.dbType.toLowerCase().includes(query)
        );
    }, [schemas, debouncedSearch]);

    // Paginate schemas locally
    const paginatedSchemas = useMemo(() => {
        const startIndex = (page - 1) * perPage;
        return filteredSchemas.slice(startIndex, startIndex + perPage);
    }, [filteredSchemas, page]);

    const columns = [
        {
            name: "Name",
            selector: (row: DatabaseSchemaItem) => row.name,
            sortable: true,
            width: "40%",
            cell: (row: DatabaseSchemaItem) => (
                <div className={styles.nameCell}>
                    <span className={styles.nameText}>{capitalize(row.name)}</span>
                </div>
            )
        },
        {
            name: "Description",
            selector: (row: DatabaseSchemaItem) => row.description || "",
            sortable: true,
            width: "25%",
            cell: (row: DatabaseSchemaItem) => (
                <span className={styles.descriptionText}>
                    {row.description || "No description provided"}
                </span>
            )
        },
        {
            name: "Type",
            selector: (row: DatabaseSchemaItem) => row.dbType,
            sortable: true,
            width: "15%",
            cell: (row: DatabaseSchemaItem) => (
                <span className={styles.typeBadge}>
                    {row.dbType}
                </span>
            )
        },
        {
            name: "Actions",
            width: "20%",
            cell: (row: DatabaseSchemaItem) => (
                <div className={styles.actionGroup}>
                    <button
                        onClick={() => handleCopyLink(row.fileUrl)}
                        className={`${styles.actionBtn} ${styles.copyBtn}`}
                        title="Copy Link"
                    >
                        <CopyIcon />
                    </button>
                    {currentUserRole !== "VIEWER" && (
                        <button
                            onClick={() => handleDeleteClick(row)}
                            className={`${styles.actionBtn} ${styles.deleteBtn}`}
                            title="Delete"
                        >
                            <DeleteIcon />
                        </button>
                    )}
                </div>
            )
        }
    ];

    return (
        <PageLayout
            title="Custom files"
            headerAction={
                currentUserRole !== "VIEWER" ? (
                    <button
                        className={styles.uploadBtn}
                        onClick={() => {
                            setEditItem(null);
                            setDbType('POSTGRESQL');
                            setDescription('');
                            setSelectedFile(null);
                            setShowModal(true);
                        }}
                    >
                        <span className={styles.uploadIcon}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: 6}}>
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                <polyline points="17 8 12 3 7 8"/>
                                <line x1="12" y1="3" x2="12" y2="15"/>
                            </svg>
                        </span>
                        Upload file
                    </button>
                ) : undefined
            }
            innerTitle="Schemas"
            innerActions={
                <SearchBox
                    value={searchTerm}
                    onChange={(val: string) => setSearchTerm(val)}
                    // placeholder="Search schemas..."
                    width={240}
                />
            }
        >
            <Table
                columns={columns as TableColumn<any>[]}
                        data={paginatedSchemas}
                        progressPending={loading}
                    />

                <Pagination
                    totalCount={filteredSchemas.length}
                    pageSelected={page}
                    setPageSelected={setPage}
                    rowsPerPageValue={perPage}
                />

            {/* Upload/Edit Modal */}
            <Modal
                heading={editItem ? "Edit database schema" : "Upload custom SQL table"}
                modalOpen={showModal}
                closeModal={() => {
                    setShowModal(false);
                    setEditItem(null);
                }}
            >
                <div className={styles.modalBodyContent}>
                    {/* Dialect selectors */}
                    {!editItem && (
                        <div className={styles.fieldGroup}>
                            <label className={styles.label}>Database Type</label>
                            <div className={styles.tabsRow}>
                                {(['POSTGRESQL', 'MYSQL', 'MSSQL', 'SQLITE'] as const).map((type) => (
                                    <button
                                        key={type}
                                        onClick={() => setDbType(type)}
                                        className={`${styles.tabBtn} ${dbType === type ? styles.tabBtnActive : ''}`}
                                    >
                                        {type === 'POSTGRESQL' ? 'PostgreSQL' : type === 'MYSQL' ? 'MySQL' : type === 'MSSQL' ? 'MSSQL' : 'SQLite'}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Description Input */}
                    <div className={styles.fieldGroup}>
                        <label className={styles.label}>Description</label>
                        <input
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Table that can be used in custom challenges."
                            className={styles.input}
                        />
                    </div>

                    {/* File Upload Selector */}
                    {!editItem && (
                        <div className={styles.uploadBox} onClick={handleUploadClick}>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".sql"
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                            />
                            <span className={styles.cloudIcon}>📤</span>
                            <p className={styles.uploadBoxText}>
                                {selectedFile ? (
                                    <strong>{selectedFile.name}</strong>
                                ) : (
                                    'No file selected (10MB limit)'
                                )}
                            </p>
                        </div>
                    )}

                    {/* Modal Footer Actions */}
                    <div className={styles.modalFooterActions}>
                        <button
                            onClick={() => {
                                setShowModal(false);
                                setEditItem(null);
                            }}
                            className={styles.cancelBtn}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleUploadSubmit}
                            disabled={uploading || (!editItem && !selectedFile)}
                            className={styles.submitBtn}
                        >
                            {uploading ? 'Uploading...' : editItem ? 'Save changes' : 'Upload SQL table'}
                        </button>
                    </div>
                </div>
            </Modal>

            <ConfirmDeleteModal
                deleteLabel="database schema"
                handleDelete={handleDelete}
            />
        </PageLayout>
    );
};

export default FileUpload;